'use client'
import { useState } from 'react'
import { MapPin, Navigation } from 'lucide-react'
import { BookingForm } from '../../app/bookings/[slug]/BookingForm'
import { SessionBookingForm } from '../../app/bookings/[slug]/SessionBookingForm'
import type { BookingProductContext } from '@/lib/bookingProductContext'
import { locationKindCopy } from '@/lib/locationKind'
import { BookingStartTracker } from './BookingStartTracker'

/**
 * The booking_widget's live region: a pickup line, an optional location
 * chooser, and the selected product's form.
 *
 * WHY the pickup line exists here and not in the form's own chrome: the embed
 * layout deliberately hides the form's overview and map, because a booking PAGE
 * carries a hero that already names the place (`formChrome`, and the WHY comment
 * on its test). A marketing page carries no such promise — the AE homepage ran
 * this widget under the heading "Learn to Fly!" and named no lake at all, so a
 * Lake Powell visitor could pay for a lesson 5.5 hours away in Heber. Rendering
 * the line at the SECTION level fixes that without touching `formChrome`, so the
 * /bookings/[slug] page stays byte-identical.
 *
 * WHY a chooser over sibling products rather than over locations: a product is
 * bound to one resource and a resource to one location, so "the same activity at
 * two lakes" is two product rows today (AE already models rentals that way). The
 * chooser therefore switches PRODUCTS, which is why each option shows its own
 * price — two siblings may differ by more than geography, and the customer has
 * to see that before they pick.
 */
export function BookingWidgetForms({ contexts }: { contexts: BookingProductContext[] }) {
  const [selected, setSelected] = useState(0)
  // Defensive: a slug that stops resolving (product deactivated) shrinks the
  // array on the next render, and a stale index would blank the whole section.
  const ctx = contexts[selected] ?? contexts[0]
  if (!ctx) return null

  const pickup = ctx.formProps.pickup
  const form =
    ctx.bookingMode === 'session' ? (
      <SessionBookingForm {...ctx.formProps} layout="embed" />
    ) : (
      <BookingForm {...ctx.formProps} layout="embed" />
    )

  return (
    <div>
      {contexts.length > 1 && (
        <div className="mb-5">
          <p className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">
            Choose a location
          </p>
          {/* Same selected-state idiom as the form's own slot and rider chips
              so one section never teaches two different "this is selected"
              languages. The SELECTION family, not the CTA: --color-cta is the
              Reserve button's color, and a chooser that borrows it competes
              with the one action on the screen that matters. */}
          <div className="flex flex-wrap gap-2">
            {contexts.map((c, i) => {
              const isSel = i === selected
              const p = c.formProps.pickup
              const label = p?.shortName ?? p?.name ?? c.formProps.product?.title ?? 'Location'
              const sub = p?.locality
              return (
                <button
                  key={c.formProps.product?.slug ?? i}
                  type="button"
                  onClick={() => setSelected(i)}
                  aria-pressed={isSel}
                  className={[
                    'min-h-[44px] rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    isSel
                      ? 'border-[var(--color-selected)] bg-[var(--color-selected)] text-[var(--color-selected-text)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-border)]/40',
                  ].join(' ')}
                >
                  <span className="block font-medium">{label}</span>
                  {sub && (
                    <span
                      className={`block text-xs ${isSel ? 'text-[var(--color-selected-text)]' : 'text-[var(--color-text-muted)]'}`}
                    >
                      {sub}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {pickup && (
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <MapPin className="h-4 w-4 shrink-0 text-[var(--color-selected-readable)]" aria-hidden="true" />
          {/* Lead with the verb. On a marketing page this single line is the
              ONLY thing telling a customer whether they meet a guide at the
              water or collect gear from a shop. */}
          <span className="text-[var(--color-text-secondary)]">
            {locationKindCopy(pickup.kind ?? 'unknown').inlineLabel}
          </span>
          <span className="font-medium text-[var(--color-text-primary)]">
            {pickup.shortName ?? pickup.name}
          </span>
          {pickup.locality && (
            <span className="text-[var(--color-text-secondary)]">· {pickup.locality}</span>
          )}
          {pickup.directionsHref && (
            <a
              href={pickup.directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-[var(--color-selected-readable)] hover:underline"
            >
              <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
              Directions
            </a>
          )}
        </div>
      )}

      {/* Remount on switch: the forms hold their own date/slot/rider state, and
          carrying a Deer Creek slot selection into the Lake Powell calendar
          would show a time that location never offered. */}
      <div key={ctx.formProps.product?.slug ?? selected}>
        <BookingStartTracker slug={ctx.formProps.product?.slug ?? ''} mode={ctx.bookingMode === 'session' ? 'session' : 'daily'}>
          {form}
        </BookingStartTracker>
      </div>
    </div>
  )
}
